import React from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { FavoriteEntity } from '@backstage/plugin-catalog-react';
import { alertApiRef } from '@backstage/core-plugin-api';
import {
  Typography,
  MaterialIcon,
  PageHeader,
  SvgIcon,
} from '@dewise-devex/backstage-plugin-mui-uikit-react';
import { Entity } from '@backstage/catalog-model';
import { StringUtil } from '@dewise-devex/backstage-plugin-utilities-common-client';

/**
 * Props for the `EntityControls` component.
 */
export type EntityControlProps = {
  /**
   * The entity to be controlled.
   */
  entity: Entity;

  /**
   * Callback to set the state of the unregister dialog.
   *
   * @param isOpen - Boolean indicating whether the dialog is open.
   */
  setUnregisterDialogOpen(isOpen: boolean): void;

  /**
   * Callback to set the state of the inspect dialog.
   *
   * @param isOpen - Boolean indicating whether the dialog is open.
   */
  setInspectDialogOpen(isOpen: boolean): void;
};

/**
 * `EntityControls` is a React component that renders the controls for managing an entity.
 *
 * - Features:
 *   - Unregister entity
 *   - Inspect entity
 *   - Copy the entity's URL to the clipboard
 *   - Displays a page header with entity information and action items
 *
 * @param props - The props for the `EntityControls` component.
 * @returns The `EntityControls` React component.
 *
 * @category Entity Menu
 */
export const EntityControls = (
  props: EntityControlProps,
): React.ReactElement => {
  const alertApi = useApi(alertApiRef);

  /**
   * Opens the unregister dialog.
   */
  const handleUnregisterOpen = () => props.setUnregisterDialogOpen(true);

  /**
   * Opens the inspect dialog.
   */
  const handleInspectOpen = () => props.setInspectDialogOpen(true);

  /**
   * Copies the entity's URL to the clipboard and shows a success or error alert.
   */
  const handleCopyEntityUrl = () => {
    const entityUrl = window.location.href;
    navigator.clipboard
      .writeText(entityUrl)
      .then(() =>
        alertApi.post({
          display: 'transient',
          message: 'Copied!',
          severity: 'info',
        }),
      )
      .catch(() =>
        alertApi.post({
          display: 'transient',
          message: 'Error copying entity url!',
          severity: 'error',
        }),
      );
  };

  const actionItems = [
    {
      label: 'Unregister entity',
      icon: <MaterialIcon.cancel />,
      onClick: handleUnregisterOpen,
    },
    {
      label: 'Inspect entity',
      icon: <MaterialIcon.bug_report />,
      onClick: handleInspectOpen,
    },
    {
      label: 'Copy entity URL',
      icon: <MaterialIcon.file_copy />,
      onClick: handleCopyEntityUrl,
    },
  ];

  let entityType = props.entity.spec?.type ?? '';
  entityType = entityType ? ` - ${String(entityType)}` : '';

  return (
    <PageHeader
      title={StringUtil.toUpperCase(`${props.entity.kind}${entityType}`)}
      icon={<SvgIcon.CodeDeployed />}
      headerActionProps={{
        actionItems: actionItems.map(({ label, ...rest }) => ({
          ...rest,
          label: <Typography variant="bodyLG">{label}</Typography>,
        })),
      }}
    >
      <Typography variant="headlineLG">
        {props.entity.metadata.title ?? props.entity.metadata.name}
      </Typography>
      <FavoriteEntity entity={props.entity} />
    </PageHeader>
  );
};
